import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CoachDigestModal({ onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [period, setPeriod] = useState("this_week");
  const [tab, setTab] = useState("preview"); // preview | schedule
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/business-coach/digest/settings");
      setSettings(data);
    } catch { /* ignore */ }
  };

  const loadPreview = async (p = period) => {
    setLoading(true);
    try {
      const { data } = await api.post("/business-coach/digest/preview", { period: p });
      setPreview(data);
    } catch (e) {
      toast.error("Failed to build digest");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadPreview(); loadSettings(); /* eslint-disable-next-line */ }, []);

  const saveSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const { data } = await api.patch("/business-coach/digest/settings", patch);
      setSettings(data);
      toast.success("Digest settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSavingSettings(false); }
  };

  const doSend = async () => {
    if (!window.confirm("Send this digest email now?")) return;
    setSending(true);
    try {
      const emails = recipients.split(",").map(s => s.trim()).filter(Boolean);
      const { data } = await api.post("/business-coach/digest/send",
        { period, ...(emails.length ? { recipients: emails } : {}) });
      const okCount = (data.recipients || []).filter(r => r.ok).length;
      const total = (data.recipients || []).length;
      if (okCount === total) toast.success(`Sent to ${total} recipient(s)`);
      else toast.error(`Sent to ${okCount}/${total} — some deliveries failed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="coach-digest-modal" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white rounded-lg overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-zinc-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono-accent uppercase tracking-widest text-[#FF5A00]">Weekly digest</div>
            <div className="font-heading text-xl font-bold">Monday Morning Briefing</div>
          </div>
          <button onClick={onClose} data-testid="coach-digest-close" className="p-2 rounded-md hover:bg-zinc-100">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="border-b border-zinc-200 px-5 flex gap-2">
          <button
            onClick={() => setTab("preview")}
            data-testid="coach-digest-tab-preview"
            className={`py-2 px-3 text-sm border-b-2 ${tab === "preview" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-zinc-500"}`}
          >Preview & send</button>
          <button
            onClick={() => setTab("schedule")}
            data-testid="coach-digest-tab-schedule"
            className={`py-2 px-3 text-sm border-b-2 ${tab === "schedule" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-zinc-500"}`}
          >Monday schedule</button>
          <button
            onClick={() => setTab("daily")}
            data-testid="coach-digest-tab-daily"
            className={`py-2 px-3 text-sm border-b-2 ${tab === "daily" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-zinc-500"}`}
          >Daily SMS</button>
          <button
            onClick={() => setTab("wrap")}
            data-testid="coach-digest-tab-wrap"
            className={`py-2 px-3 text-sm border-b-2 ${tab === "wrap" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-zinc-500"}`}
          >Friday wrap</button>
        </div>

        <div className="p-5 space-y-3 border-b border-zinc-200" style={{ display: tab === "preview" ? "block" : "none" }}>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Period</span>
            {["this_week", "this_month", "last_30_days"].map((p) => (
              <button key={p}
                      onClick={() => { setPeriod(p); loadPreview(p); }}
                      data-testid={`coach-digest-period-${p}`}
                      className={`px-2 py-1 text-xs rounded-md ${
                        period === p ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                      }`}>
                {p.replaceAll("_", " ")}
              </button>
            ))}
          </div>
          <Input
            placeholder="Recipients (comma-separated). Leave blank to send to business owners."
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            data-testid="coach-digest-recipients"
          />
        </div>

        <div className="flex-1 overflow-y-auto bg-zinc-100 p-4" style={{ display: tab === "preview" ? "block" : "none" }}>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500 justify-center py-16">
              <Loader2 className="w-4 h-4 animate-spin"/> Building digest…
            </div>
          )}
          {preview && !loading && (
            <>
              <div className="text-xs text-zinc-500 mb-2 font-mono-accent uppercase tracking-wider">Subject: {preview.subject}</div>
              <iframe
                title="Digest preview"
                srcDoc={preview.html}
                data-testid="coach-digest-preview-iframe"
                className="w-full h-[520px] bg-white rounded-md border border-zinc-200"
              />
            </>
          )}
        </div>

        <div className="border-t border-zinc-200 px-5 py-3 flex items-center justify-end gap-2" style={{ display: tab === "preview" ? "flex" : "none" }}>
          <Button variant="outline" onClick={onClose} data-testid="coach-digest-cancel">Close</Button>
          <Button
            onClick={doSend}
            disabled={sending || loading}
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
            data-testid="coach-digest-send"
          >
            {sending ? "Sending…" : <><Send className="w-4 h-4 mr-1"/> Send now</>}
          </Button>
        </div>

        {tab === "schedule" && (
          <ScheduleTab settings={settings} onSave={saveSettings} saving={savingSettings}/>
        )}
        {tab === "daily" && <DailyTab/>}
        {tab === "wrap" && <WrapTab/>}
      </div>
    </div>
  );
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function ScheduleTab({ settings, onSave, saving }) {
  const [form, setForm] = useState({
    enabled: false, day_of_week: 0, send_hour: 7, send_minute: 0,
    timezone: "America/Los_Angeles", recipients: "",
  });
  useEffect(() => {
    if (settings) setForm({
      enabled: !!settings.enabled,
      day_of_week: settings.day_of_week ?? 0,
      send_hour: settings.send_hour ?? 7,
      send_minute: settings.send_minute ?? 0,
      timezone: settings.timezone ?? "America/Los_Angeles",
      recipients: (settings.recipients || []).join(", "),
    });
  }, [settings]);
  const submit = () => {
    onSave({
      enabled: form.enabled,
      day_of_week: parseInt(form.day_of_week, 10),
      send_hour: parseInt(form.send_hour, 10),
      send_minute: parseInt(form.send_minute, 10),
      timezone: form.timezone,
      recipients: form.recipients.split(",").map(s => s.trim()).filter(Boolean),
    });
  };
  return (
    <div className="p-6 space-y-4 flex-1 overflow-y-auto" data-testid="coach-digest-schedule-tab">
      <div className="bg-orange-50 border border-orange-200 text-sm text-orange-900 rounded-md p-3">
        <b>Automatic sending is not enabled yet.</b> Save your preferred schedule below, then have your operator create the scheduler entry when you're ready to turn on recurring delivery.
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          data-testid="coach-digest-enabled"
        />
        <span>Enable weekly digest for this business</span>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500">Day of week</label>
          <select
            value={form.day_of_week}
            onChange={(e) => setForm({ ...form, day_of_week: e.target.value })}
            data-testid="coach-digest-day"
            className="w-full mt-1 border border-zinc-200 rounded-md px-3 py-2 text-sm"
          >
            {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500">Timezone</label>
          <Input
            value={form.timezone}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            data-testid="coach-digest-timezone"
            className="mt-1"
            placeholder="America/Los_Angeles"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500">Hour (0-23)</label>
          <Input type="number" min={0} max={23}
                 value={form.send_hour}
                 onChange={(e) => setForm({ ...form, send_hour: e.target.value })}
                 data-testid="coach-digest-hour"
                 className="mt-1"/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500">Minute (0-59)</label>
          <Input type="number" min={0} max={59}
                 value={form.send_minute}
                 onChange={(e) => setForm({ ...form, send_minute: e.target.value })}
                 data-testid="coach-digest-minute"
                 className="mt-1"/>
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider text-zinc-500">Extra recipients (comma-separated)</label>
        <Input
          value={form.recipients}
          onChange={(e) => setForm({ ...form, recipients: e.target.value })}
          data-testid="coach-digest-recipients-schedule"
          className="mt-1"
          placeholder="owner@example.com, ops@example.com"
        />
        <div className="text-xs text-zinc-500 mt-1">Owners are always included. This adds more.</div>
      </div>
      <div className="text-xs text-zinc-500">
        {settings?.last_sent_at && <>Last sent: {settings.last_sent_at.replace("T", " ").slice(0, 16)} · </>}
        Status: <b>{form.enabled ? "Enabled" : "Disabled (no auto-send)"}</b>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={submit}
          disabled={saving}
          className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
          data-testid="coach-digest-schedule-save"
        >
          {saving ? "Saving…" : "Save schedule"}
        </Button>
      </div>
    </div>
  );
}


function DailyTab() {
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    const s = (await api.get("/business-coach/notifications/settings")).data;
    setSettings(s);
    const p = (await api.post("/business-coach/notifications/daily-priority/preview")).data;
    setPreview(p);
    const h = (await api.get("/business-coach/notifications/history?kind=daily_priority")).data;
    setHistory(h);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/business-coach/notifications/settings",
        { daily_priority: settings.daily_priority });
      toast.success("Daily SMS settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const sendNow = async () => {
    if (!window.confirm("Send today's #1 priority SMS to the owner now?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/business-coach/notifications/daily-priority/send");
      if (data.ok) toast.success("Sent");
      else toast.info(data.reason || "Nothing to send");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  if (!settings) return <div className="p-6 text-sm text-zinc-500">Loading…</div>;
  const d = settings.daily_priority;
  const setD = (patch) => setSettings({ ...settings, daily_priority: { ...d, ...patch } });
  return (
    <div className="p-6 space-y-4 overflow-y-auto flex-1" data-testid="coach-digest-daily-tab">
      <div className="bg-orange-50 border border-orange-200 text-sm text-orange-900 rounded-md p-3">
        Sends ONE highest-priority action to the owner's phone. Never sends filler. Automatic recurring send is off until an operator schedules it.
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!d.enabled}
               onChange={(e) => setD({ enabled: e.target.checked })}
               data-testid="coach-daily-enabled"/>
        <span>Enable Daily Priority SMS</span>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs uppercase tracking-wider text-zinc-500">Recipient phone</label>
          <Input value={d.recipient_phone || ""} onChange={(e) => setD({ recipient_phone: e.target.value })}
                 data-testid="coach-daily-phone" placeholder="+15551234567"/></div>
        <div><label className="text-xs uppercase tracking-wider text-zinc-500">Timezone</label>
          <Input value={d.timezone} onChange={(e) => setD({ timezone: e.target.value })}
                 data-testid="coach-daily-timezone"/></div>
        <div><label className="text-xs uppercase tracking-wider text-zinc-500">Send hour</label>
          <Input type="number" min={0} max={23} value={d.send_hour}
                 onChange={(e) => setD({ send_hour: parseInt(e.target.value, 10) })}
                 data-testid="coach-daily-hour"/></div>
        <div><label className="text-xs uppercase tracking-wider text-zinc-500">Send minute</label>
          <Input type="number" min={0} max={59} value={d.send_minute}
                 onChange={(e) => setD({ send_minute: parseInt(e.target.value, 10) })}
                 data-testid="coach-daily-minute"/></div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={sendNow} disabled={sending}
                data-testid="coach-daily-send-now">
          {sending ? "Sending…" : "Send test SMS now"}
        </Button>
        <Button onClick={save} disabled={saving}
                className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
                data-testid="coach-daily-save">
          {saving ? "Saving…" : "Save daily settings"}
        </Button>
      </div>
      {preview && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-sm" data-testid="coach-daily-preview">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Right now, we would send:</div>
          {preview.has_priority
            ? <pre className="whitespace-pre-wrap font-mono text-xs">{preview.text}</pre>
            : <div className="text-zinc-500 italic">Nothing worth interrupting the owner for today.</div>}
        </div>
      )}
      {history.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">History</div>
          <ul className="space-y-1 max-h-40 overflow-y-auto text-xs">
            {history.slice(0, 15).map(h => (
              <li key={h.id} className="flex items-start gap-2 border-b border-zinc-100 py-1">
                <span className={h.sent ? "text-emerald-600" : "text-zinc-400"}>●</span>
                <span className="w-24 text-zinc-500">{h.day_key}</span>
                <span className="flex-1 truncate">{h.sent ? h.text : `(${h.reason})`}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function WrapTab() {
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const load = async () => {
    setSettings((await api.get("/business-coach/notifications/settings")).data);
    setPreview((await api.post("/business-coach/notifications/weekly-wrap/preview")).data);
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/business-coach/notifications/settings", { weekly_wrap: settings.weekly_wrap });
      toast.success("Weekly wrap settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const sendNow = async () => {
    if (!window.confirm("Send Friday Wrap email now?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/business-coach/notifications/weekly-wrap/send", {});
      if (data.ok) toast.success("Sent");
      else toast.info(data.reason || "Nothing sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };
  if (!settings) return <div className="p-6 text-sm text-zinc-500">Loading…</div>;
  const w = settings.weekly_wrap;
  const setW = (patch) => setSettings({ ...settings, weekly_wrap: { ...w, ...patch } });
  return (
    <div className="p-6 space-y-4 overflow-y-auto flex-1" data-testid="coach-digest-wrap-tab">
      <div className="bg-orange-50 border border-orange-200 text-sm text-orange-900 rounded-md p-3">
        Friday afternoon recap of everything the Coach helped with. Automatic recurring send is off until scheduled.
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!w.enabled}
               onChange={(e) => setW({ enabled: e.target.checked })}
               data-testid="coach-wrap-enabled"/>
        <span>Enable Friday Weekly Wrap</span>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs uppercase tracking-wider text-zinc-500">Recipient email</label>
          <Input value={w.recipient_email || ""} onChange={(e) => setW({ recipient_email: e.target.value })}
                 data-testid="coach-wrap-email" placeholder="owner@example.com"/></div>
        <div><label className="text-xs uppercase tracking-wider text-zinc-500">Timezone</label>
          <Input value={w.timezone} onChange={(e) => setW({ timezone: e.target.value })}
                 data-testid="coach-wrap-timezone"/></div>
        <div><label className="text-xs uppercase tracking-wider text-zinc-500">Day</label>
          <select value={w.day_of_week}
                  onChange={(e) => setW({ day_of_week: parseInt(e.target.value, 10) })}
                  data-testid="coach-wrap-day"
                  className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) =>
              <option key={i} value={i}>{d}</option>)}
          </select></div>
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" value={w.send_hour}
                 onChange={(e) => setW({ send_hour: parseInt(e.target.value, 10) })}
                 data-testid="coach-wrap-hour" placeholder="H"/>
          <Input type="number" value={w.send_minute}
                 onChange={(e) => setW({ send_minute: parseInt(e.target.value, 10) })}
                 data-testid="coach-wrap-minute" placeholder="M"/>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={sendNow} disabled={sending}
                data-testid="coach-wrap-send-now">
          {sending ? "Sending…" : "Send test wrap now"}
        </Button>
        <Button onClick={save} disabled={saving}
                className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
                data-testid="coach-wrap-save">
          {saving ? "Saving…" : "Save wrap settings"}
        </Button>
      </div>
      {preview && (
        <iframe title="Wrap preview" srcDoc={preview.html}
                data-testid="coach-wrap-preview-iframe"
                className="w-full h-[420px] bg-white rounded-md border border-zinc-200"/>
      )}
    </div>
  );
}
